/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import ProjectIcon from '@atlaskit/icon/core/project';
import ReleaseIcon from '@atlaskit/icon/core/release';
import IconTile from '@atlaskit/icon/icon-tile';
import Image from '@atlaskit/image';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	JIRA_PLANS_CREATE_RELEASE_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { JiraPlansCreateReleasePayload } from '@atlassian/conversation-assistant-message-actions-api/jira';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import { type MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { EntityCard } from '../../common/ui/entity-card';
import { EntityTitle } from '../../common/ui/entity-title';
import { EntityValueGrid, EntityValueGridRow } from '../../common/ui/entity-value-grid';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';
import { jiraPlansActionData } from '../../common/utils/plan-action-data';

import messages from './messages';

const INTERACTION_SOURCE = 'jiraPlansCreateReleaseRenderer';

const styles = cssMap({
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: token('space.100'),
	},
});

const parsePlanDate = (date?: string): Date | undefined => {
	if (!date) {
		return undefined;
	}

	const [year, month, day] = date.split('-').map(Number);
	if (!year || !month || !day) {
		return undefined;
	}

	return new Date(year, month - 1, day);
};

export const filterCreateReleaseActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansCreateRelease]>[] => {
	const filteredActions = actions.filter((action) => assertIsCreateReleaseAction(action));
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansCreateRelease]
	>[];
};

const assertIsCreateReleaseAction = (action: MessageAction): MessageAction => {
	if (!jiraPlansActionData.isPlanActionKeyForRenderer(action.key, JIRA_PLANS_CREATE_RELEASE_KEY)) {
		throw new Error('Invalid action key for CreateReleaseAction');
	}
	return action;
};

export const CreateReleaseAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansCreateRelease>,
): JSX.Element | null => {
	const { actions } = props;

	const firstAction = useMemo(() => filterCreateReleaseActions(actions)[0], [actions]);
	if (!firstAction) {
		return null;
	}

	return <CreateReleaseActionContent {...props} firstAction={firstAction} />;
};

const CreateReleaseActionContent = (
	props: ActionRendererFor<typeof ActionKeys.JiraPlansCreateRelease> & {
		firstAction: ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraPlansCreateRelease]>;
	},
) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage, formatDate } = useIntl();

	const {
		getStatus,
		getReadonly,
		firstAction,
		onConfirm,
		onCancel,
		actions,
		sendMessageConfirmedTools,
		recipientAgentId,
		recipientAgentNamedId,
		conversationId,
		messageId,
	} = props;

	const isCustomAgent = !isDefaultRovoChat({
		agentId: recipientAgentId,
		agentNamedId: recipientAgentNamedId,
	});

	const { data } = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const invocationStatus = String(status ?? '').toLowerCase();
	const actionStatus = String(firstAction.status ?? '').toLowerCase();

	const isActionLoading = invocationStatus !== 'idle';
	const isSubmitting = invocationStatus === 'started';
	const isActionFinished = invocationStatus === 'finished' || actionStatus === 'finished';
	const buttonsDisabled = isActionLoading || readonly;

	const actionData = jiraPlansActionData.getPlanActionData<JiraPlansCreateReleasePayload>(data);

	const handleConfirm = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: 'rovo-chat-jira-plans-create-release-confirm',
				planId: actionData.planId,
				scenarioId: actionData.scenarioId,
				name: actionData.name,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed')
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onConfirm(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		onConfirm,
		isCustomAgent,
	]);

	const handleCancel = useCallback(() => {
		sendAnalyticsEvent({
			actionSubjectId: firstAction.actionId,
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				actionSubjectId: 'rovo-chat-jira-plans-create-release-cancel',
				planId: actionData.planId,
				scenarioId: actionData.scenarioId,
			},
		});

		if (
			fg('jira_plans_rovo_tools_resumption_migration') ||
			(isCustomAgent && fg('enable_skills_ga_tools_in_studio'))
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: { interactionSource: INTERACTION_SOURCE },
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) => buildToolFromAction(action, 'cancelled')),
				},
			});
			return;
		}

		onCancel(firstAction);
	}, [
		firstAction,
		actions,
		actionData,
		getStatus,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		sendAnalyticsEvent,
		onCancel,
		isCustomAgent,
	]);

	const space = useMemo(
		() => (
			<Inline space="space.100">
				{actionData.projectAvatarUrl ? (
					<Tile label="" size="xsmall" isInset={false}>
						<Image src={actionData.projectAvatarUrl} alt="" />
					</Tile>
				) : (
					<IconTile label="" appearance="gray" size="xsmall" icon={ProjectIcon} />
				)}
				<Text maxLines={1}>{actionData.projectName}</Text>
			</Inline>
		),
		[actionData.projectAvatarUrl, actionData.projectName],
	);

	const startDate = parsePlanDate(actionData.start);
	const releaseDate = parsePlanDate(actionData.end);

	const startDateValue = startDate
		? formatDate(startDate, { year: 'numeric', month: 'numeric', day: 'numeric' })
		: formatMessage(messages.asEarlyAsPossible);

	const releaseDateValue = releaseDate
		? formatDate(releaseDate, { year: 'numeric', month: 'numeric', day: 'numeric' })
		: formatMessage(messages.noReleaseDate);

	if (!actionData.name) {
		return null;
	}

	return (
		<ActionCardWrapper isExpandable title={formatMessage(messages.cardTitle)} product="plan">
			<EntityCard>
				<EntityTitle
					icon={<IconTile label="" appearance="gray" size="small" icon={ReleaseIcon} />}
					title={actionData.name}
				/>
				<EntityValueGrid>
					{actionData.projectName && (
						<EntityValueGridRow label={`${formatMessage(messages.spaceLabel)}:`} value={space} />
					)}
					<EntityValueGridRow
						label={`${formatMessage(messages.startDateLabel)}:`}
						value={startDateValue}
					/>
					<EntityValueGridRow
						label={`${formatMessage(messages.releaseDateLabel)}:`}
						value={releaseDateValue}
					/>
				</EntityValueGrid>
			</EntityCard>

			{!isActionFinished && (
				<ActionCardFooter>
					<Box xcss={styles.footer}>
						<Button appearance="default" onClick={handleCancel} isDisabled={buttonsDisabled}>
							{formatMessage(messages.cancelButton)}
						</Button>
						<Button
							appearance="primary"
							onClick={handleConfirm}
							isDisabled={buttonsDisabled}
							isLoading={isSubmitting}
						>
							{formatMessage(messages.confirmButton)}
						</Button>
					</Box>
				</ActionCardFooter>
			)}
		</ActionCardWrapper>
	);
};
